import { depositBonusService } from '@/services/depositBonusService'
import { referralService } from '@/services/referralService'
import { voucherCompletionService } from '@/services/voucherCompletionService'
import { StatusCode } from '@/types/agent/transaction'
import type { VendorBonusClaim } from '@/types/pos/depositBonus'
import type { CompleteVoucherResponse } from '@/types/pos/referral'
import { normalizePromoCode } from '@/utilities/referralHelper'

export type PendingDepositRewardJob = {
    walletId: number
    identifier: string
    expectedRewardAmount: number
    expectedRewardCurrency: string
} & (
    | { kind: 'vendor'; rewardVoucherToken: string }
    | { kind: 'referral'; contextToken: string; promoCode: string }
)

export const getDepositRewardJob = ({
    response,
    walletId,
    identifier,
    vendorBonusEnabled,
    referralEnabled,
    promoCode,
    vendorBonusClaim
}: {
    response: CompleteVoucherResponse
    walletId: number
    identifier: string
    vendorBonusEnabled: boolean
    referralEnabled: boolean
    promoCode: string
    vendorBonusClaim?: VendorBonusClaim | null
}): PendingDepositRewardJob | null => {
    const currency = response.moneyTransfer.currencyCode || 'RSD'
    const vendorToken = response.depositBonus?.rewardVoucherToken

    // The backend awards one bonus source. A vendor award excludes a referral claim.
    if (vendorToken) {
        if (!vendorBonusEnabled) return null
        return {
            kind: 'vendor',
            walletId,
            identifier,
            rewardVoucherToken: vendorToken,
            expectedRewardAmount:
                response.depositBonus?.amount ??
                vendorBonusClaim?.rewardAmount ??
                0,
            expectedRewardCurrency:
                response.depositBonus?.currencyCode ??
                vendorBonusClaim?.currencyCode ??
                currency
        }
    }

    const referral = response.referral
    const normalizedCode = normalizePromoCode(promoCode)
    if (
        !referralEnabled ||
        !referral?.canClaimReferralCode ||
        !referral.contextToken ||
        normalizedCode.length !== 4
    ) {
        return null
    }

    return {
        kind: 'referral',
        walletId,
        identifier,
        contextToken: referral.contextToken,
        promoCode: normalizedCode,
        expectedRewardAmount: referralService.DEFAULT_REFERRED_REWARD_AMOUNT,
        expectedRewardCurrency: currency
    }
}

/** Only completed reward deposits contribute to the animation's credited total. */
export const settleDepositRewardJob = async (
    job: PendingDepositRewardJob
): Promise<number> => {
    const applyRewardVoucher = async (
        voucherCode: string | undefined,
        amount: number | undefined
    ): Promise<number> => {
        if (
            !voucherCode ||
            amount == null ||
            !Number.isFinite(amount) ||
            amount <= 0
        ) {
            return 0
        }

        const { moneyTransfer } =
            await voucherCompletionService.completeVoucher({
                walletId: job.walletId,
                identifier: job.identifier,
                voucherCode,
                amount
            })

        if (
            moneyTransfer?.statusCode !== StatusCode.completed ||
            !Number.isFinite(moneyTransfer.amount) ||
            moneyTransfer.amount <= 0
        ) {
            return 0
        }

        return moneyTransfer.amount
    }

    if (job.kind === 'vendor') {
        try {
            const minted = await depositBonusService.mintRewardVoucher(
                job.rewardVoucherToken
            )
            return await applyRewardVoucher(
                minted.voucher?.voucherCode,
                minted.voucher?.amount ?? minted.amount
            )
        } catch {
            console.error('Failed to apply vendor deposit bonus')
            return 0
        }
    }

    try {
        const claimed = await referralService.claimReferralCode({
            contextToken: job.contextToken,
            referralCode: job.promoCode
        })
        if (claimed.rewardVoucherToken) {
            const exchanged = await referralService.exchangeRewardVoucher({
                rewardVoucherToken: claimed.rewardVoucherToken
            })
            return await applyRewardVoucher(
                exchanged.voucher?.voucherCode,
                exchanged.voucher?.amount
            )
        }
    } catch {
        console.error('Failed to apply referral reward voucher')
    }

    return 0
}
